import { englishCanonicalUrl } from "@/config/canonical";
import { GAME_NAME } from "@/config/game";
import { mixpanelTrack } from "@/events/mixpanelTrack";
import {
  Article,
  STRAPI_URL,
  formatArticleDate,
  imgUrl,
  visibilityFilter,
} from "@/integrations/strapi-cms/utils";
import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import axios from "axios";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { NavLink } from "react-router";
import { useMediaQuery } from "usehooks-ts";

const NEWS_TITLE = `EA ${GAME_NAME} News – EasySBC`;
const NEWS_DESCRIPTION = `Get the latest EA Sports ${GAME_NAME} news, tips, and releases from EasySBC. Stay ahead with insights from our pro experts and exclusive data on ${GAME_NAME} trends.`;

const Blog = () => {
  const isTouch = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    mixpanelTrack("news_page_viewed");
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let alive = true;

    (async () => {
      try {
        setLoading(true);

        const environmentFilter = visibilityFilter(window.location.hostname);

        const articlesApiUrl =
          `${STRAPI_URL}/api/articles` +
          `?publicationState=live` +
          `&sort[0]=publishedAt:desc` +
          environmentFilter +
          `&populate[cover]=true` +
          `&populate[author]=true` +
          `&populate[category]=true`;

        const res = await axios.get<{ data: Article[] }>(articlesApiUrl, {
          signal: controller.signal,
        });
        if (!alive) return;
        setArticles(res.data?.data ?? []);
      } catch (error) {
        console.error(error);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
      controller.abort();
    };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen w-full bg-panel-base" />
    );
  }

  const featured = articles.slice(0, 2);
  const others = articles.slice(2);

  return (
    <div className="bg-panel-base min-h-screen">
      <Helmet>
        <title>{NEWS_TITLE}</title>
        <meta name="description" content={NEWS_DESCRIPTION} />
        <meta property="og:title" content={NEWS_TITLE} />
        <meta property="og:description" content={NEWS_DESCRIPTION} />
        <link rel="canonical" href={englishCanonicalUrl("/news")} />
      </Helmet>
      <div
        className={cn(
          "w-full max-w-content mx-auto space-y-10",
          isTouch ? "py-4 px-3" : "py-10 px-4 sm:px-6 lg:px-12",
        )}
      >
        <div className="flex flex-col">
          <span className={cn("text-lg font-bold text-dark-primary")}>
            EA {GAME_NAME} News
          </span>
          <span
            className={cn(
              "font-light text-dark-secondary",
              isTouch ? "text-sm" : "text-base",
            )}
          >
            Get the latest EA Sports {GAME_NAME} news, tips, and releases from
            EasySBC. Stay ahead with insights from our pro experts and access
            exclusive data on {GAME_NAME} trends.
          </span>
        </div>

        {featured.length > 0 && (
          <div
            className={cn(
              "grid gap-6",
              isTouch ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2",
            )}
          >
            {featured.map((a) => {
              const url = imgUrl(a.cover);
              return (
                <NavLink
                  key={a.id}
                  className="cursor-pointer overflow-hidden rounded-md group"
                  to={`/news/${a.slug || a.id}/`}
                >
                  {url && (
                    <div className="overflow-hidden">
                      <img
                        src={url}
                        alt={a.cover?.alternativeText || a.title}
                        className={cn(
                          "w-full object-cover transition-transform duration-300 group-hover:scale-105",
                          isTouch ? "h-36" : "h-56 sm:h-72 md:h-96",
                        )}
                      />
                    </div>
                  )}
                  <div
                    className={cn(
                      "flex flex-col justify-between bg-new-gray-800 p-3 transition-colors duration-200 group-hover:bg-new-gray-750",
                      isTouch ? "h-40" : "min-h-44",
                    )}
                  >
                    <div>
                      <h2 className="text-lg text-dark-secondary font-semibold transition-colors duration-200 group-hover:text-dark-primary">
                        {a.title}
                      </h2>
                      {a.description && (
                        <p
                          className={cn(
                            "text-dark-tertiary transition-colors duration-200 group-hover:text-dark-secondary",
                            isTouch ? "text-xs" : "text-sm",
                          )}
                        >
                          {a.description}
                        </p>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 flex gap-1 justify-end group-hover:text-dark-secondary">
                      {/* {a.category?.name && <>#{a.category.name}</>} */}
                      {a.author?.name && <span>{a.author.name},</span>}
                      {a.publishedAt && (
                        <span>{formatArticleDate(a.publishedAt)}</span>
                      )}
                    </div>
                  </div>
                </NavLink>
              );
            })}
          </div>
        )}

        <div className={cn("border border-panel-background")} />

        {others.length > 0 && (
          <ul
            className={cn(
              "grid gap-6",
              isTouch
                ? "grid-cols-1"
                : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
            )}
          >
            {others.map((a) => {
              const url = imgUrl(a.cover);
              return (
                <li
                  key={a.id}
                  className="overflow-hidden cursor-pointer rounded-md group"
                >
                  <NavLink to={`/news/${a.slug || a.id}`}>
                    {url && (
                      <div className="overflow-hidden">
                        <img
                          src={url}
                          alt={a.cover?.alternativeText || a.title}
                          className={cn(
                            "w-full object-cover transition-transform duration-300 group-hover:scale-105",
                            isTouch ? "h-36" : "h-48 sm:h-64",
                          )}
                        />
                      </div>
                    )}
                    <div
                      className={cn(
                        "flex flex-col justify-between bg-new-gray-800 p-3 transition-colors duration-200 group-hover:bg-new-gray-750",
                        isTouch ? "h-40" : "min-h-60",
                      )}
                    >
                      <div>
                        <h2 className="text-lg text-dark-tertiary font-semibold transition-colors duration-200 group-hover:text-dark-primary">
                          {a.title}
                        </h2>
                        {a.description && (
                          <p
                            className={cn(
                              "text-dark-tertiary transition-colors duration-200 group-hover:text-dark-secondary",
                              isTouch ? "text-xs" : "text-sm",
                            )}
                          >
                            {a.description}
                          </p>
                        )}
                      </div>
                      <div className="text-xs text-dark-tertiary group-hover:text-dark-secondary flex gap-1 justify-end">
                        {a.author?.name && <span>{a.author.name},</span>}
                        {a.publishedAt && (
                          <span>{formatArticleDate(a.publishedAt)}</span>
                        )}
                      </div>
                    </div>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Blog;
